import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import CpuPanel from './CpuPanel';
import DisksPanel from './DisksPanel';
import MemoryPanel from './MemoryPanel';
import NetworkPanel from './NetworkPanel';
import StatsTabs, { type TabDefinition } from './StatsTabs';
import { fetchSystemStats } from './api';
import {
  chartSampleCounts,
  chartTimestamps,
  estimateSampleInterval,
  hasAggregatedSamples,
} from './series';
import type { Sample, SystemStatsProps, TabId } from './types';

/**
 * Matches the processor's own snapshot cadence. ServerQueues polls at 5s, but
 * here that would fetch the same hundred-entry map twice per new sample.
 */
const POLL_INTERVAL = 10000;

const TABS: TabDefinition[] = [
  { id: 'cpu', label: 'CPU' },
  { id: 'memory', label: 'Memory' },
  { id: 'disks', label: 'Disks' },
  { id: 'network', label: 'Network' },
];

/** Poll one server's system stats and graph them, grouped into tabs. */
export default function SystemStats({
  serverId,
  pollInterval = POLL_INTERVAL,
}: SystemStatsProps) {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Tab state lives here, alongside the polled data, precisely so a poll cannot
  // disturb it: a re-render never resets sibling state. The invariant that
  // keeps that true is that nothing on the path down to StatsTabs carries a
  // `key` that varies with the data.
  const [selectedTab, setSelectedTab] = useState<TabId>('cpu');

  // Here rather than in DisksPanel for the same reason, plus one more: the panel
  // is unmounted when another tab is selected, so a local choice would be lost
  // every time an admin looked at CPU and came back.
  const [selectedDisk, setSelectedDisk] = useState<string | null>(null);

  const rawId = useId().replace(/:/g, '');
  const tabElementId = useCallback((id: TabId) => `${rawId}-tab-${id}`, [rawId]);
  const panelElementId = useCallback((id: TabId) => `${rawId}-panel-${id}`, [rawId]);

  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);

  const poll = useCallback(async () => {
    // The admin router puts a 5s timeout on this route, so an unreachable
    // processor makes every request take that long. Without this guard they
    // would stack up, and this payload is far larger than the queues one.
    if (inFlightRef.current) return;

    inFlightRef.current = true;

    try {
      const next = await fetchSystemStats(serverId);

      if (!mountedRef.current) return;

      // Replace, never merge. The processor downsamples rather than evicting,
      // and re-bucketing means an entry keyed 1060 can later be folded into one
      // keyed 1000 - merging by key would keep the stale 1060 alongside the
      // bucket that already contains it, double-counting the overlap.
      setSamples(next);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;

      // Keep the last good data on screen and keep polling - a transient
      // failure shouldn't blank the charts.
      setError(err instanceof Error ? err.message : 'Failed to get system stats.');
    } finally {
      inFlightRef.current = false;

      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [serverId]);

  useEffect(() => {
    mountedRef.current = true;

    // Poll immediately, then on interval
    poll();
    timerRef.current = setInterval(poll, pollInterval);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [poll, pollInterval]);

  const timestamps = useMemo(() => chartTimestamps(samples), [samples]);
  const sampleCounts = useMemo(() => chartSampleCounts(samples), [samples]);
  const intervalSeconds = useMemo(() => estimateSampleInterval(samples), [samples]);
  const aggregated = useMemo(() => hasAggregatedSamples(samples), [samples]);

  if (loading) {
    return <div className="spinner centered" />;
  }

  return (
    <div className="system-stats">
      {error && (
        <div className="error-alert" role="alert">
          {error}
        </div>
      )}

      <StatsTabs
        tabs={TABS}
        selected={selectedTab}
        onSelect={setSelectedTab}
        tabElementId={tabElementId}
        panelElementId={panelElementId}
      />

      <div
        className="system-stats__panel"
        role="tabpanel"
        id={panelElementId(selectedTab)}
        aria-labelledby={tabElementId(selectedTab)}
        // The panel holds no focusable children, and a keyboard user still has
        // to be able to scroll the Disks tab.
        tabIndex={0}
      >
        {samples.length === 0 ? (
          <p className="system-stats__empty">
            No data yet. The server records a snapshot every ten seconds.
          </p>
        ) : (
          <>
            {selectedTab === 'cpu' && (
              <CpuPanel samples={samples} timestamps={timestamps} sampleCounts={sampleCounts} />
            )}

            {selectedTab === 'memory' && (
              <MemoryPanel samples={samples} timestamps={timestamps} sampleCounts={sampleCounts} />
            )}

            {selectedTab === 'disks' && (
              <DisksPanel
                samples={samples}
                timestamps={timestamps}
                sampleCounts={sampleCounts}
                intervalSeconds={intervalSeconds}
                selectedDisk={selectedDisk}
                onSelectDisk={setSelectedDisk}
              />
            )}

            {selectedTab === 'network' && (
              <NetworkPanel
                samples={samples}
                timestamps={timestamps}
                sampleCounts={sampleCounts}
                intervalSeconds={intervalSeconds}
              />
            )}

            {aggregated && (
              <p className="system-stats__note">
                Older readings are averaged into wider buckets, marked on the charts. The server
                keeps a fixed number of entries and coarsens history rather than discarding it.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
